import React, { useEffect } from 'react';
import { Mountain, ArrowLeft, ExternalLink, Cloud, MapPin, Gauge, Sun, Mountain as MountainIcon, WifiOff, Share2, Wind, Apple, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const RoamWeather: React.FC = () => {
  const weatherAppUrl = 'https://weather.roamapps.com';
  const appStoreUrl = 'https://apps.apple.com/app/roam-weather/id6760034245';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.roamapps.weather';

  useEffect(() => {
    document.title = 'Roam Weather | Multi-Location Forecasts for Outdoor Adventures';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Compare multi-day weather forecasts for multiple locations at once, with idealness scoring, elevation adjustments, and smoke & air quality forecasts for any point on the map.'
      );
    }
  }, []);

  const features = [
    {
      icon: MapPin,
      title: 'Multi-Location Forecasts',
      description: 'Save all your favorite trailheads, crags, and summits and compare their forecasts side by side — no more tab-switching between locations.'
    },
    {
      icon: Gauge,
      title: 'Idealness Score',
      description: 'Define what "good weather" means for your activity — temperature range, wind, precipitation — and see every forecast day scored from 0–100%.'
    },
    {
      icon: Wind,
      title: 'Smoke & Air Quality Forecasts',
      description: 'Hourly smoke AQI forecasts up to 96 hours out, for any point location in the US, Canada, North America, and Europe.'
    },
    {
      icon: MountainIcon,
      title: 'Elevation-Adjusted Temperatures',
      description: 'Forecasts are adjusted to your location\'s actual elevation, not the nearest weather grid point — critical for alpine routes.'
    },
    {
      icon: Sun,
      title: 'Sunlight-Adjusted "Feels Like"',
      description: 'A sunny 45°F day feels different from a cloudy one. Idealness scoring accounts for solar radiation, not just the thermometer.'
    },
    {
      icon: WifiOff,
      title: 'Offline Access',
      description: 'Install the iOS or Android app and keep your cached forecasts available even without cell service in the backcountry.'
    },
    {
      icon: Share2,
      title: 'Share Forecasts Instantly',
      description: 'Send a shareable forecast link for any coordinates — no account required for your friends to view it.'
    }
  ];

  const useCases = [
    {
      icon: MountainIcon,
      title: 'Rock Climbing & Alpine Routes',
      description: 'Compare crags side by side and get an idealness score tuned to climbing conditions.'
    },
    {
      icon: MapPin,
      title: 'Backpacking Trip Planning',
      description: 'Scan multiple trailheads at once to find the best weather window for a multi-day route.'
    },
    {
      icon: Wind,
      title: 'Wildfire Smoke Planning',
      description: 'Check hourly smoke AQI up to 4 days out so a hazy trip doesn\'t sneak up on you.'
    },
    {
      icon: Cloud,
      title: 'Any Outdoor Adventure',
      description: 'From weekend hikes to international expeditions, Roam Weather covers any point location worldwide.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <Mountain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Roam</h1>
                <p className="text-sm text-gray-600">Outdoor Adventure Apps</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-700 hover:text-blue-600 transition-colors">
                Blog
              </Link>
              <a
                href={weatherAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                Try Weather
                <ExternalLink className="h-4 w-4" />
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="flex justify-center mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-7 rounded-3xl shadow-xl">
              <Cloud className="h-14 w-14 text-white" />
            </div>
          </div>

          <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            Web &amp; Mobile &nbsp;·&nbsp; Free to Start
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Multi-day forecasts for every<br className="hidden sm:block" /> place you want to explore
          </h2>

          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Roam Weather lets you compare multi-day forecasts for multiple locations at once — with idealness scoring, elevation adjustments, and now smoke &amp; air quality forecasts, for any point on the map.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={weatherAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
            >
              Try Roam Weather — It's Free
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
            >
              <Apple className="h-5 w-5" />
              Download on the App Store
            </a>
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
            >
              <PlayCircle className="h-5 w-5" />
              Get it on Google Play
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to plan around the weather
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built for outdoor enthusiasts who need to compare conditions across multiple locations, not just check the forecast where they already are.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-blue-100">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 rounded-xl w-fit mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Perfect for any outdoor adventure
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Whether you're comparing crags, planning a multi-day trip, or checking for wildfire smoke, Roam Weather has you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 flex items-start gap-4">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-3 rounded-xl flex-shrink-0">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{useCase.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing / Offline callout */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-10 sm:p-14 text-center text-white">
            <WifiOff className="h-10 w-10 mx-auto mb-4 opacity-90" />
            <h3 className="text-3xl font-bold mb-4">Free to start. Pay only when you need more.</h3>
            <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Save up to 6 locations for free, no account required. Premium unlocks unlimited saved locations for $1.29/month (or $5.99/year, or a one-time $11.99 for lifetime access) — all plans are non-recurring.
            </p>
            <a
              href={weatherAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all shadow-lg"
            >
              Try Roam Weather — It's Free
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
              <Mountain className="h-6 w-6 text-white" />
            </div>
            <span className="text-lg font-semibold">Roam Apps</span>
          </Link>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">iOS App</a>
            <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Android App</a>
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
          <p className="text-gray-500 text-sm">© 2026 Roam. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
